<template>
	<label
		ref="labelRef"
		class="relative flex flex-col md:justify-center w-full md:w-[calc(20%-20px)] min-h-[56px] md:min-h-[60px] pl-0 sm:pl-4 md:pl-6 pr-0 sm:pr-4 pb-0 sm:pb-2 md:pb-0 border-b-[1px] border-gray-light md:border-0"
		@keydown="navigateInResults"
	>
		<span class="pb-1 text-dj-gray-dark text-sm font-medium">{{ $t("companies.searchbar.search.title") }}</span>
		<input
			ref="inputRef"
			v-model="freeWord"
			class="appearance-none outline-none bg-transparent"
			@keydown="preventKeys"
			type="text"
			:placeholder="placeholder"
			spellcheck="false"
			autocomplete="false"
			autocorrect="off"
		/>

		<div
			v-if="showSuggestions && freeWord"
			class="absolute left-0 top-[60px] md:top-[85px] w-[400px] max-w-[100%] md:max-w-[100vw] bg-white border border-dj-gray-light rounded-big flex flex-col z-30"
		>
			<div class="flex flex-col p-dj-20">
				<h3 class="font-medium mb-[10px]">{{ $t("companies.searchbar.search.companies") }}</h3>
				<div v-if="companiesResult" class="max-h-[40vh] overflow-y-auto">
					<div
						@click="goToCompany(hit)"
						:class="[
							'flex items-center w-full mb-[10px] last-of-type:mb-0 cursor-pointer',
							navIndex === i ? 'selected' : '',
						]"
						v-for="(hit, i) in filteredCompanies"
						:key="hit.objectID"
					>
						<span v-if="hit.logo" class="min-w-[30px] min-h-[30px] mr-[10px]">
							<BaseCloudImage
								:transformation="[{ width: 30, height: 30, crop: 'scale', radius: 10 }]"
								:public-id="hit.logo"
								:alt="`Logo ${hit.name}`"
							/>
						</span>
						<span class="block text-sm" v-html="highlightText(hit.name)"></span>
					</div>
				</div>
			</div>
		</div>
	</label>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import { onClickOutside, useFocus } from "@vueuse/core";

import keys from "../../../enums/keys";

export default defineComponent({
	name: "SearchbarCompaniesAutocomplete",
	props: {
		modelValue: {
			type: String,
			default: "",
		},
		hitsPerPage: {
			type: Number,
			default: 20,
		},
		placeholder: {
			type: String,
			default: "",
		},
	},
	emits: ["update:modelValue"],
	setup(props, { emit }) {
		// input v-model
		const freeWord = ref("");

		// label/input field template ref
		const labelRef = ref();
		const inputRef = ref();

		const showSuggestions = ref(false);
		const filteredCompanies = ref([]);

		// input focus state
		const { focused: inputFocused } = useFocus(inputRef);

		const { locale } = useI18n();

		// algolia
		const { result: companiesResult, search: searchCompanies } = useAlgoliaSearch("companies");

		const router = useRouter();

		const algoliaRegionFilter = ref("facet_region:0");

		// watch input focus state
		watch(inputFocused, (focused) => {
			focused ? (showSuggestions.value = true) : null;
		});

		onMounted(() => {
			const region = useRegion.getCurrentRegion().code;
			algoliaRegionFilter.value = region == "HU" ? "facet_region:0" : "facet_region:1";
		});

		// watch input v-model
		watch(freeWord, async (value) => {
			freeWord.value = value.replace(/[<>;"']/g, ""); // xss protection
			emit("update:modelValue", freeWord.value);
			await searchCompanies({
				query: value,
				requestOptions: { hitsPerPage: props.hitsPerPage, facetFilters: [algoliaRegionFilter.value] },
			});
			navIndex.value = 0;
			setSelectedSuggestion();
		});

		onClickOutside(labelRef, () => {
			showSuggestions.value = false;
		});

		function preventKeys(event) {
			// prevent keys
			if (
				event.keyCode === keys.ARROW_UP ||
				event.keyCode === keys.ARROW_DOWN ||
				event.keyCode === keys.TAB ||
				event.keyCode === keys.ENTER
			)
				event.preventDefault();

			if (event.keyCode === keys.TAB || event.keyCode === keys.ENTER) {
				if (navIndex.value == 0) {
					const query = {
						...(freeWord.value.length != 0 && { keyword: freeWord.value }),
					};
					navigateTo({
						path: `/${locale.value}/companies`,
						query: query,
					});
				} else {
					freeWord.value = selectedSuggestion.value;
					selectedSuggestion.value = "";
					showSuggestions.value = false;
					emit("update:modelValue", freeWord.value);
					inputFocused.value = false;
				}
			}

			if (event.keyCode === keys.ENTER && selectedSuggestionUrl.value && navIndex.value !== 0) {
				router.push(selectedSuggestionUrl.value);
			}
		}

		// current index
		const navIndex = ref(0);

		watch(navIndex, () => {
			setSelectedSuggestion();
		});

		function navigateInResults(evt) {
			if (evt.keyCode === keys.ARROW_UP || evt.keyCode === keys.ARROW_DOWN) {
				// max value of indexes
				const maxValue = companiesResult.value.hits.length - 1;
				switch (evt.keyCode) {
					case keys.ARROW_UP:
						if (navIndex.value === 0) navIndex.value = maxValue;
						else navIndex.value--;
						break;
					case keys.ARROW_DOWN:
						if (navIndex.value === maxValue) navIndex.value = 0;
						else navIndex.value++;
						break;
				}
			}
		}

		const selectedSuggestion = ref("");
		const selectedSuggestionUrl = ref("");

		function setSelectedSuggestion() {
			if (
				navIndex.value >= 0 &&
				navIndex.value <= companiesResult.value.hits.length - 1 &&
				companiesResult.value.hits.length !== 0
			) {
				selectedSuggestion.value = companiesResult.value.hits[navIndex.value].name;
				selectedSuggestionUrl.value = companiesResult.value.hits[navIndex.value].url;
			}
		}

		// function setSelectedSuggestionOnClick(suggestion: string) {
		// 	freeWord.value = suggestion;
		// 	selectedSuggestion.value = "";
		// 	showSuggestions.value = false;
		// 	inputFocused.value = false;
		// }

		function goToCompany(company) {
			if (company.url) {
				router.push(`/${locale.value}${company.url}`);
			}
		}

		function highlightText(text: string) {
			const cleanFreeWord = freeWord.value.replace(/[^a-zA-Z0-9]/g, "");
			return text.replace(new RegExp(cleanFreeWord, "gi"), (match) => {
				return "<mark class='highlight'>" + match + "</mark>";
			});
		}

		watch(companiesResult, (value) => {
			filteredCompanies.value = value.hits;
			filteredCompanies.value.unshift({ name: freeWord.value, objectID: "0" });
		});

		onMounted(() => {
			const query = useRoute().query;
			if (query.keyword) {
				freeWord.value = query.keyword.toString();
			}
		});

		return {
			freeWord,
			preventKeys,
			companiesResult,
			highlightText,
			labelRef,
			inputRef,
			showSuggestions,
			navIndex,
			navigateInResults,
			selectedSuggestion,
			// setSelectedSuggestionOnClick,
			filteredCompanies,
			goToCompany,
		};
	},
});
</script>

<style lang="scss" scoped>
.highlight {
	@apply text-dj-red font-semibold bg-transparent;
}

.selected {
	@apply text-dj-red;
}
</style>
